import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { TableList, TextButton, useOverlayContext } from 'seagull';
import { useStore } from '~/contexts';
import { FeatureFlag } from '~/featureFlags';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { EditMode, NotificationSettingsEntryType } from '~/models/NotificationSettingsModel';
import { selectors } from '~/store/state-selectors';
import { NotificationEditModal } from './components/NotificationEditModal/NotificationEditModal';
import { NotificationSettingsTableV2 } from './components/NotificationSettingsTableV2/NotificationSettingsTableV2';
import { EditNotificationSettingsModal } from './EditNotificationSettingsModal';
import { useSetNotificationStatusV2 } from './hooks/useSetNotificationStatusV2';
import { getNotificationsToShow, notificationSettingsTextsPath, Table } from './NotificationSettings.utils';
import styles from './NotificationSettingsTable.module.scss';
const typeMap = {
    [Table.Reminders]: NotificationSettingsEntryType.Recurring,
    [Table.Notifications]: NotificationSettingsEntryType.Single
};
export const NotificationSettingsTable = (param)=>{
    let { data, tableType, columnDefs, selectedTab, headerItems, refresh, getRowItems } = param;
    const featureFlags = useFeatureFlags();
    const overlayContext = useOverlayContext();
    const { t: texts } = useLocaleKeys(notificationSettingsTextsPath);
    const { t: commonTexts } = useLocaleKeys('common');
    const businessId = useStore(selectors.user.businessId);
    const [pendingNotificationName, setPendingNotificationName] = useState(null);
    const [isRefreshingAfterToggle, setIsRefreshingAfterToggle] = useState(false);
    const { setStatus, isLoading: isSettingStatus } = useSetNotificationStatusV2({
        onSuccess: async ()=>{
            // Keep loader visible until refresh lands so toggle state and loader disappear together.
            setIsRefreshingAfterToggle(true);
            try {
                await refresh({
                    silent: true
                });
            } finally{
                setIsRefreshingAfterToggle(false);
                setPendingNotificationName(null);
            }
        },
        onError: ()=>{
            setPendingNotificationName(null);
        }
    });
    const showKarbonIntegration = useStore(selectors.integration.showKarbonIntegration);
    const showClientHubIntegration = useStore(selectors.integration.showClientHubIntegration);
    const showFinancialCentsIntegration = useStore(selectors.integration.showFinancialCentsIntegration);
    const showKeeperIntegration = useStore(selectors.integration.showKeeperIntegration);
    const notificationsTextMap = getNotificationsToShow({
        tab: selectedTab,
        showKarbonIntegration,
        showClientHubIntegration,
        showFinancialCentsIntegration,
        showKeeperIntegration,
        featureFlags,
        texts
    });
    const handleEditClick = (props)=>{
        overlayContext.showModal({
            biName: 'edit_notification_settings_modal',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(EditNotificationSettingsModal, _object_spread({
                    selectedTab: selectedTab,
                    dismissModal: dismissModal
                }, props));
            }
        });
    };
    const isV2 = featureFlags === null || featureFlags === void 0 ? void 0 : featureFlags[FeatureFlag.NOTIFICATION_CONTENT_CUSTOMIZATION];
    const handleEditClickV2 = (args)=>{
        overlayContext.showModal({
            biName: 'notification_edit_modal',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(NotificationEditModal, {
                    notificationName: args.notificationName,
                    notification: args.notification,
                    notificationLabel: args.notificationLabel,
                    selectedTab: selectedTab,
                    onClose: ()=>{
                        dismissModal();
                        refresh();
                    },
                    onRevertSuccess: async ()=>{
                        const latest = await refresh({
                            silent: true
                        });
                        return latest[selectedTab][args.notificationName];
                    }
                });
            }
        });
    };
    const isToggleMutationInFlight = isSettingStatus || isRefreshingAfterToggle;
    const handleToggleClickV2 = (args)=>{
        if (businessId == null) return;
        // Guard: a second toggle during mutation+refresh would clobber the pending row's loader and clear it on the wrong onSuccess.
        if (isToggleMutationInFlight) return;
        setPendingNotificationName(args.notificationName);
        setStatus({
            businessId,
            notificationName: args.notificationName,
            status: args.nextStatus
        });
    };
    if (isV2) {
        return /*#__PURE__*/ _jsx(NotificationSettingsTableV2, {
            data: data,
            tableType: tableType,
            selectedTab: selectedTab,
            onEditClick: handleEditClickV2,
            onToggleClick: handleToggleClickV2,
            pendingNotificationName: pendingNotificationName,
            isLoading: isToggleMutationInFlight
        });
    }
    return /*#__PURE__*/ _jsxs(TableList.Provider, {
        columnDefs: columnDefs,
        hasActionColumn: true,
        children: [
            /*#__PURE__*/ _jsx(TableList.Header, {
                items: headerItems,
                testId: "".concat(tableType, "-table-header"),
                className: styles.header
            }),
            Object.entries(notificationsTextMap).map((param)=>{
                let [key, text] = param;
                const notification = data[selectedTab][key];
                return notification && notification.type === typeMap[tableType] && /*#__PURE__*/ _jsx(TableList.Row, {
                    size: "fluid",
                    items: getRowItems({
                        notification,
                        text,
                        key
                    }),
                    testId: "".concat(tableType, "-row-").concat(key),
                    className: styles.row,
                    actionColumn: notification.editMode === EditMode.Enabled && /*#__PURE__*/ _jsx(TextButton, {
                        onClick: ()=>handleEditClick({
                                tableType,
                                notification,
                                notificationText: text,
                                notificationName: key,
                                refresh
                            }),
                        underline: "none",
                        testId: "".concat(tableType, "-row-").concat(key, "-edit"),
                        children: commonTexts.buttons.edit()
                    })
                }, key);
            })
        ]
    });
};
